<template>
  <div class="full-height">
    <v-card width="100%" rounded="0" class="full-height">
      <div class="profile-page">
        <div id="header" class="profile-page__header">
          <v-icon large dark class="pa-2 brand-background border-radius">mdi-account</v-icon>
          <Typography variant="h-1" as="span">My Account</Typography>
        </div>
        <v-divider />
        <div class="profile-page__body">
          <div class="profile-page__sidebar">
            <div class="popular-links">
              <Typography variant="subtitle-1">Popular links</Typography>
              <Button
                text
                color="brandPrimary"
                @click="resetOnboardingFlow"
                v-if="hasPermissionToManageClients()"
              >
                Reset Onboarding (Demo)
              </Button>
              <Button
                text
                color="brandPrimary"
                @click="userTokenDialog = true"
                v-if="showDevButtons"
              >
                View my token
              </Button>
              <Button
                v-if="
                  userProfile &&
                  userProfile.Client &&
                  userProfile.Client.displayWalletPage &&
                  permissions.includes('vue:read:wallet')
                "
                text
                color="brandPrimary"
                @click="openWallet"
              >
                View my wallet
              </Button>
              <Button text color="brandPrimary" @click="downloadPrivacy">Privacy Policy</Button>
              <Button text color="brandPrimary" @click="downloadTerms">Terms of Service</Button>
              <Button text color="brandPrimary" @click="openMailto(`mailto:${supportEmail}`)">
                Contact Us
              </Button>
              <Button text color="brandPrimary" @click="gdprDialog = true">GDPR Request</Button>
              <SlackDownload v-if="displaySlackButton" :userId="userProfile.userId" />
            </div>
            <v-divider></v-divider>
            <p
              v-if="userProfile && userProfile.Client.customProfileMessage"
              v-html="userProfile.Client.customProfileMessage"
              class="py-3 text-left"
            ></p>
            <div
              class="align-text-left d-flex flex-column py-2"
              v-if="!$vuetify.breakpoint.sm && !$vuetify.breakpoint.xs"
            >
              <Typography variant="subtitle-1" class="mb-2">
                Learning Badges ({{ badges.filter((x) => x?.Badge?.context == "LEARNING").length }})
              </Typography>
              <!-- We hide the badges for anyone without a client image for now, because that is the most reliable way to hide it from non-Demo clients -->
              <div class="py-2 d-flex flex-wrap">
                <div
                  v-for="badge in badges.filter((x) => x?.Badge?.context == 'LEARNING')"
                  :key="badge.badgeId"
                  class="cursor-pointer"
                >
                  <v-img
                    v-if="badge?.Badge?.imageUrl"
                    :src="badge.Badge.imageUrl"
                    :max-width="65"
                    :height="65"
                    contain
                    class="mx-2 mt-1"
                    @click="loadBadgePage(badge.badgeId)"
                  />
                  <v-img
                    v-else
                    src="@/assets/Demo/Trophy_Badge.png"
                    :max-width="65"
                    :height="65"
                    contain
                    class="mx-2 mt-1"
                    @click="loadBadgePage(badge.badgeId)"
                  />
                </div>
              </div>
              <Typography variant="subtitle-1" class="mb-2">
                Program Badges ({{ badges.filter((x) => x?.Badge?.context == "PROGRAM").length }})
              </Typography>
              <div class="py-2 d-flex flex-wrap">
                <div
                  v-for="badge in badges.filter((x) => x?.Badge?.context == 'PROGRAM')"
                  :key="badge.badgeId"
                  class="cursor-pointer"
                >
                  <v-img
                    v-if="badge?.Badge?.imageUrl"
                    :src="badge.Badge.imageUrl"
                    :max-width="65"
                    :height="65"
                    contain
                    class="mx-2 mt-1 cursor-pointer"
                    @click="loadBadgePage(badge.badgeId)"
                  />
                  <v-img
                    v-else
                    src="@/assets/Demo/Trophy_Badge.png"
                    :max-width="65"
                    :height="65"
                    contain
                    class="mx-2 mt-1 cursor-pointer"
                    @click="loadBadgePage(badge.badgeId)"
                  />
                </div>
              </div>
              <div></div>
            </div>
          </div>
          <div class="profile-page__content">
            <v-tabs v-model="tab" show-arrows color="brandPrimary">
              <v-tabs-slider color="brandPrimary"></v-tabs-slider>
              <v-tab :key="1"> Profile </v-tab>
              <!-- <v-tab :key="2"> Settings </v-tab> -->
              <v-tab :key="2"> Communication </v-tab>
              <!-- <v-tab :key="3"> Dashboard </v-tab> -->
              <v-tab :key="3"> Badges </v-tab>
              <v-tab :key="4"> Awards </v-tab>
            </v-tabs>
            <v-divider />
            <v-tabs-items v-model="tab">
              <v-tab-item :key="1">
                <div class="profile-tab">
                  <div class="profile-tab__form">
                    <Typography variant="h-4" class="profile-form__heading">Basic Info</Typography>
                    <Alert
                      v-if="informManaged"
                      class="full-width"
                      variant="info"
                      :message="informAlertMessage"
                    />
                    <v-form v-model="validForm1" class="profile-form">
                      <div class="profile-form__row">
                        <v-text-field
                          outlined
                          hide-details="auto"
                          v-model="user.firstName"
                          :disabled="fieldReadonly('firstName')"
                          label="First name*"
                          :rules="[
                            (v) => !!v || 'First name is required',
                            (v) => !!(v && v.length < 100) || 'First name should be shorter',
                            formRules.noHtml('First name'),
                          ]"
                        ></v-text-field>
                        <v-text-field
                          outlined
                          hide-details="auto"
                          v-model="user.lastName"
                          :disabled="fieldReadonly('lastName')"
                          label="Last name*"
                          :rules="[
                            (v) => !!v || 'Last name is required',
                            (v) => !!(v && v.length < 100) || 'Last name should be shorter',
                            formRules.noHtml('Last name'),
                          ]"
                        ></v-text-field>
                      </div>

                      <v-text-field
                        outlined
                        hide-details="auto"
                        v-model="user.displayName"
                        :disabled="fieldReadonly('displayName')"
                        label="Preferred name"
                        :rules="[
                          (v) => !v || v.length < 100 || 'Preferred name should be shorter',
                          formRules.noHtml('Preferred name'),
                        ]"
                      ></v-text-field>

                      <div class="profile-form__half">
                        <v-menu
                          ref="birthdayMenu"
                          v-model="user.birthdayMenu"
                          :close-on-content-click="false"
                          transition="scale-transition"
                          offset-y
                          min-width="auto"
                          :disabled="fieldReadonly('birthday')"
                        >
                          <template v-slot:activator="{ on, attrs }">
                            <v-text-field
                              v-model="user.birthday"
                              label="Birthday"
                              readonly
                              :disabled="fieldReadonly('birthday')"
                              outlined
                              hide-details="auto"
                              v-bind="attrs"
                              v-on="on"
                            ></v-text-field>
                          </template>
                          <v-date-picker
                            ref="birthdayPicker"
                            v-model="user.birthday"
                            :max="new Date().toISOString().substr(0, 10)"
                            :disabled="fieldReadonly('birthday')"
                            @change="saveBirthday"
                          ></v-date-picker>
                        </v-menu>
                      </div>

                      <div class="profile-form__row">
                        <v-text-field
                          outlined
                          hide-details="auto"
                          :disabled="true"
                          v-model="user.businessEmail"
                          label="Primary Email*"
                        ></v-text-field>
                        <v-text-field
                          outlined
                          hide-details="auto"
                          v-model="user.personalEmail"
                          :disabled="fieldReadonly('personalEmail')"
                          label="Secondary Email"
                          :rules="
                            user.personalEmail
                              ? [
                                  (v) =>
                                    !!(
                                      !v ||
                                      (v &&
                                        /^\w+([&.+\-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,24})+$/.test(v))
                                    ) || 'Email must be valid',
                                ]
                              : []
                          "
                        ></v-text-field>
                      </div>

                      <div class="profile-form__row">
                        <PhoneNumber
                          label="Phone number"
                          placeholder="xxx-xxx-xxxx"
                          :disabled="true"
                          outlined
                          class="full-width"
                          :value="user.businessPhone"
                          @validate="businessPhoneValidation"
                          @input="updateBusinessPhone"
                        />
                        <PhoneNumber
                          label="Additional phone number"
                          placeholder="xxx-xxx-xxxx"
                          :disabled="fieldReadonly('personalPhone')"
                          outlined
                          class="full-width"
                          :value="user.personalPhone"
                          @validate="personalPhoneValidation"
                          @input="updatePersonalPhone"
                        />
                      </div>

                      <div class="profile-form__row">
                        <v-text-field
                          v-if="userProfile"
                          outlined
                          disabled
                          hide-details="auto"
                          v-model="userProfile.jobStartDate"
                          label="Job Start Date"
                        ></v-text-field>
                        <v-text-field
                          v-if="userProfile"
                          outlined
                          disabled
                          hide-details="auto"
                          v-model="userProfile.roleStartDate"
                          label="Role Start Date"
                        ></v-text-field>
                      </div>

                      <v-select
                        outlined
                        disabled
                        hide-details="auto"
                        class="profile-form__half"
                        :items="languageOptions"
                        item-text="name"
                        item-value="code"
                        label="Preferred Language"
                        v-model="user.language"
                      ></v-select>
                      <div class="home-address">
                        <Typography variant="subtitle-1" class="profile-form__heading"
                          >Home Address</Typography
                        >
                        <v-text-field
                          outlined
                          hide-details="auto"
                          v-model="user.homeAddress"
                          :disabled="fieldReadonly('homeAddress')"
                          :label="homeAddressFieldLabel('Street Address')"
                          :rules="homeAddressRules('Street Address')"
                          maxlength="255"
                        ></v-text-field>
                        <v-text-field
                          outlined
                          hide-details="auto"
                          class="home-address__half"
                          v-model="user.homeAddress2"
                          :disabled="fieldReadonly('homeAddress2')"
                          label="Apartment, suite, etc. (optional)"
                          :rules="[
                            (v) => !v || v.length < 255 || 'This should be shorter',
                            formRules.noHtml('Apartment, suite, etc.'),
                          ]"
                          maxlength="255"
                        ></v-text-field>
                        <v-text-field
                          outlined
                          hide-details="auto"
                          v-model="user.homeCity"
                          :disabled="fieldReadonly('homeCity')"
                          :label="homeAddressFieldLabel('City')"
                          :rules="homeAddressRules('City')"
                          maxlength="255"
                        ></v-text-field>
                        <div class="home-address__row">
                          <v-select
                            v-if="isUSHomeAddress"
                            outlined
                            hide-details="auto"
                            v-model="user.homeState"
                            :items="states"
                            item-text="name"
                            item-value="abbreviation"
                            :disabled="fieldReadonly('homeState')"
                            :label="homeAddressFieldLabel('State / Province')"
                            :rules="homeAddressRules('State / Province')"
                            :menu-props="{ bottom: true, offsetY: true }"
                          ></v-select>
                          <v-text-field
                            v-else
                            outlined
                            hide-details="auto"
                            v-model="user.homeState"
                            :disabled="fieldReadonly('homeState')"
                            :label="homeAddressFieldLabel('State / Province')"
                            :rules="homeAddressRules('State / Province')"
                            maxlength="255"
                          ></v-text-field>
                          <v-text-field
                            outlined
                            hide-details="auto"
                            v-model="user.homePostalCode"
                            :disabled="fieldReadonly('homePostalCode')"
                            :label="homeAddressFieldLabel('Zip / Postal Code')"
                            :rules="homeAddressRules('Zip / Postal Code')"
                            maxlength="255"
                          ></v-text-field>
                        </div>
                        <v-select
                          outlined
                          hide-details="auto"
                          v-model="user.homeCountry"
                          :items="countries"
                          item-text="name"
                          item-value="code"
                          :disabled="fieldReadonly('homeCountry')"
                          :label="homeAddressFieldLabel('Country')"
                          :rules="homeAddressRules('Country')"
                          :menu-props="{ bottom: true, offsetY: true }"
                        ></v-select>
                        <v-text-field
                          v-if="isOtherHomeCountry"
                          outlined
                          hide-details="auto"
                          v-model="user.otherHomeCountry"
                          :disabled="fieldReadonly('homeCountry')"
                          :label="homeAddressFieldLabel('Country (2 digit code)')"
                          :rules="otherHomeCountryRules"
                          maxlength="2"
                        ></v-text-field>
                      </div>
                      <Button
                        v-if="
                          userProfile && userProfile.passwordEnabled && userProfile.businessEmail
                        "
                        text
                        class="align-self-start"
                        color="brandPrimary"
                        @click="resetPassword"
                        :loading="submittingPasswordRequest"
                      >
                        Reset My Password
                      </Button>
                      <v-expansion-panels v-if="canCollectTaxInfo" v-model="taxPanel" flat>
                        <v-expansion-panel>
                          <v-expansion-panel-header
                            class="profile-form__heading"
                            style="font-size: 1.5rem; font-weight: 700; padding: 0"
                            >Tax Info</v-expansion-panel-header
                          >
                          <v-expansion-panel-content class="tax-info-content">
                            <TaxInfoSection
                              ref="taxInfoSection"
                              :has-tax-info="hasTaxInfo"
                              :saved-tax-type-code="savedTaxTypeCode"
                              :user-profile="userProfile"
                              @update="onTaxUpdate"
                            />
                          </v-expansion-panel-content>
                        </v-expansion-panel>
                      </v-expansion-panels>
                      <div class="profile-form__save">
                        <Button
                          color="brandPrimary"
                          :disabled="disableSaveButton"
                          :loading="submittingChanges"
                          @click="saveProfile"
                        >
                          Save Changes
                        </Button>
                      </div>
                    </v-form>
                  </div>
                  <div class="profile-tab__photo" v-if="userProfile !== null">
                    <v-btn
                      v-if="user.profilePicUrl == null || user.isSelectingProfilePic"
                      depressed
                      :loading="user.isSelectingProfilePic"
                      @click="displayImageSelector"
                      class="ma-2 image-button"
                      fab
                      medium
                      color="primary"
                      @drop.prevent="dragAndDropImageFileChanged"
                      @dragover.prevent
                    >
                      <v-icon x-large> mdi-camera </v-icon>
                    </v-btn>
                    <div v-else class="image-container">
                      <v-img
                        :src="user.profilePicUrl"
                        height="150"
                        width="150"
                        max-height="150"
                        max-width="150"
                        @click="displayImageSelector"
                        @drop.prevent="dragAndDropImageFileChanged"
                        @dragover.prevent
                        class="cursor-pointer circle-border-radius mx-auto image-border"
                      />
                      <v-btn
                        icon
                        dark
                        small
                        class="pa-4 delete-image-button"
                        @click="deleteProfilePicture"
                        ><v-icon>mdi-close</v-icon></v-btn
                      >
                    </div>

                    <input
                      ref="uploader"
                      class="d-none"
                      type="file"
                      accept="image/*"
                      @change="imageFileChanged"
                    />
                  </div>
                </div>
              </v-tab-item>
              <v-tab-item :key="2">
                <v-card flat rounded="0" class="d-flex justify-start align-start flex-column pa-4">
                  <Typography variant="subtitle-1">Preferences</Typography>
                  <div class="d-flex py-3 px-5 mt-2 flex-column align-start preference-box">
                    <v-switch
                      v-model="communicationPreferences.emailPreference"
                      label="Email notifications"
                      hide-details
                    ></v-switch>
                    <v-switch
                      v-model="communicationPreferences.smsPreference"
                      label="Text notifications"
                      hide-details
                      class="mt-10"
                    ></v-switch>
                    <a
                      class="mt-2 ml-11"
                      :class="{ hidden: !communicationPreferences.smsPreference }"
                      href="#"
                      @click.prevent="dialog.smsTroubleshoot = true"
                    >
                      <Typography variant="body-2" as="span">Still not getting texts?</Typography>
                    </a>
                    <v-switch
                      v-model="communicationPreferences.pushPreference"
                      label="Push notifications"
                      hide-details
                      class="mt-2"
                    ></v-switch>
                    <Button
                      class="mt-6"
                      color="brandPrimary"
                      :disabled="!checkIfCommPreferencesChanged"
                      @click="saveCommunicationPreferences"
                    >
                      Save Changes
                    </Button>
                  </div>
                </v-card>
              </v-tab-item>
              <v-tab-item :key="3">
                <v-card flat class="d-flex flex-wrap">
                  <v-progress-circular
                    indeterminate
                    color="primary"
                    :size="50"
                    :width="3"
                    class="mx-auto mt-5"
                    v-if="loading.badges"
                  />
                  <v-card
                    v-if="badges.length == 0 && !loading.badges"
                    flat
                    class="align-text-left px-4 pt-4 full-width"
                  >
                    <Typography variant="body-1" class="word-break">
                      Once you start earning badges, this is where they'll be listed!
                    </Typography>
                    <v-img
                      src="@/assets/Placeholder_Screens/Winner.png"
                      width="40%"
                      min-width="250"
                      max-width="350"
                      class="mx-auto"
                    />
                  </v-card>
                  <v-card
                    v-for="badge in badges"
                    :key="badge.badgeId"
                    min-width="300"
                    width="30%"
                    class="mx-3 my-3 pt-4"
                    @click="badge.showDescription = !badge.showDescription"
                    min-height="225"
                  >
                    <div
                      class="d-flex flex-column justify-space-between align-center full-height"
                      v-if="!badge.showDescription"
                    >
                      <Typography variant="subtitle-1">{{ badge?.Badge?.displayName }}</Typography>

                      <v-img
                        v-if="badge?.Badge?.imageUrl"
                        :src="badge.Badge.imageUrl"
                        :max-width="80"
                        :height="80"
                        contain
                        class="mx-auto my-1"
                      />
                      <v-img
                        v-else
                        src="@/assets/Demo/Trophy_Badge.png"
                        :max-width="80"
                        :height="80"
                        contain
                        class="mx-auto my-1"
                      />
                      <Typography variant="body-2" as="p" class="mb-0">
                        Last earned {{ badge.formattedDate }}
                      </Typography>
                    </div>
                    <div v-else class="d-flex flex-column align-center">
                      <div class="d-flex align-center justify-start">
                        <v-img
                          v-if="badge?.Badge?.imageUrl"
                          :src="badge.Badge.imageUrl"
                          :max-width="35"
                          :height="35"
                          contain
                          class="mr-2 my-1"
                        />
                        <v-img
                          v-else
                          src="@/assets/Demo/Trophy_Badge.png"
                          :max-width="35"
                          :height="35"
                          contain
                          class="mr-2 my-1"
                        />
                        <Typography
                          v-if="badge?.Badge?.displayName?.length < 25"
                          variant="subtitle-1"
                          class="align-text-left"
                        >
                          {{ badge?.Badge?.displayName }}
                        </Typography>
                        <v-tooltip top v-else>
                          <template v-slot:activator="{ on, attrs }">
                            <Typography
                              variant="subtitle-1"
                              v-bind="attrs"
                              v-on="on"
                              class="align-text-left"
                            >
                              {{ badge?.Badge?.displayName?.substring(0, 25) + ".." }}
                            </Typography>
                          </template>
                          <Typography variant="body-2" as="span">{{
                            badge?.Badge?.displayName
                          }}</Typography>
                        </v-tooltip>
                      </div>
                      <Typography
                        variant="body-1"
                        as="p"
                        class="align-text-left mx-4 mt-2 word-break"
                      >
                        {{ badge?.Badge?.description }}
                      </Typography>
                    </div>
                  </v-card></v-card
                >
              </v-tab-item>
              <v-tab-item :key="4">
                <!-- <v-card flat class="d-flex flex-wrap max-width full-width"> -->
                <v-card flat rounded="0" class="d-flex flex-column justify-start align-start pa-4">
                  <Typography variant="subtitle-1">
                    Awards ({{
                      balances.filter((x) => parseFloat(x.balance).toFixed(2) > 0).length
                    }})
                  </Typography>
                  <v-card
                    v-for="award in balances.filter((x) => parseFloat(x.balance).toFixed(2) > 0)"
                    :key="award.asset_code"
                    min-width="50%"
                    width="30%"
                    class="mx-3 my-3 py-3"
                    min-height="100"
                  >
                    <div class="d-flex py-3 px-5 align-center justify-space-between">
                      <div class="d-flex flex-column align-text-left">
                        <Typography variant="h-4" as="h3">
                          {{
                            awards.find((x) => award.asset_code == x.stellarAssetCode)
                              ? awards.find((x) => award.asset_code == x.stellarAssetCode)
                                  .displayName
                              : "Award"
                          }}
                        </Typography>
                        <Typography variant="subtitle-1" as="h4" class="mt-2">
                          Balance:
                          {{ award.balance ? parseFloat(award.balance).toFixed(2) : 0 }}
                        </Typography>
                      </div>
                    </div>
                  </v-card>
                </v-card>
              </v-tab-item>
            </v-tabs-items>
          </div>
        </div>
      </div>
    </v-card>
    <!-- Navigation confirmation dialog -->
    <v-dialog v-model="showNavigationDialog" persistent max-width="400">
      <v-card>
        <div class="d-flex align-center mx-6">
          <v-icon color="error" x-large class="exit-warning-icon">mdi-alert-circle</v-icon>
          <v-card-title class="text-h6" style="font-weight: 500"
            >Are you sure you want to exit?</v-card-title
          >
        </div>
        <v-card-text class="mb-4 text-left body-1 pl-8">
          You will lose any unsaved changes.
        </v-card-text>
        <v-card-actions class="justify-end">
          <Button text color="brandPrimary" @click="handleNavigationCancel">GO BACK</Button>
          <Button color="brandPrimary" @click="handleNavigationDiscard" style="width: 120px">
            EXIT
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Dialog to show confirmation after resetting password -->
    <v-dialog v-model="passwordResetDialog" persistent width="400">
      <v-card class="py-3">
        <v-card-text class="d-flex flex-column justify-center align-center pt-4">
          <Typography variant="h-5" as="h2">Your password reset is on the way!</Typography>

          <Typography variant="body-1" as="p" class="my-3">
            Be on the lookout for your password reset email.
          </Typography>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <Button color="brandPrimary" class="mr-2" @click="passwordResetDialog = false">
            Close
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Dialog to show confirmation after resetting password -->
    <v-dialog v-model="emailErrorDialog" persistent width="350">
      <v-card class="py-3">
        <Typography variant="h-5" as="h2" class="mt-2 d-flex align-center">
          <v-icon class="mr-2" color="error">mdi-alert</v-icon>
          Duplicate email found
        </Typography>
        <v-card-text class="d-flex flex-column justify-center align-center">
          <Typography variant="body-1" as="p" class="my-3">
            Uh oh! It looks like we already found a user with this email address.
          </Typography>
          <Typography variant="body-1" as="p">Please try using another email.</Typography>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <Button color="brandPrimary" class="mr-2" @click="emailErrorDialog = false">
            Close
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Dialog to show success after linking to Slack -->
    <v-dialog v-model="slackDialog" persistent width="500">
      <v-card class="py-3">
        <Typography variant="h-5" as="h2" class="mt-2 d-flex align-center flex-wrap">
          <v-icon
            class="mr-2 circle-border-radius check-icon"
            :class="{ red: slackError, 'brand-green-background': !slackError }"
            large
            color="white"
          >
            {{ slackError ? "mdi-close" : "mdi-check" }}
          </v-icon>
          {{ slackError ? "We encountered a problem" : "Nicely done!" }}
        </Typography>
        <v-card-text class="mt-4 d-flex flex-column justify-center align-center">
          <Typography variant="body-1" as="p" class="my-3" v-if="slackError">
            There was a problem linking your {{ applicationName }} and Slack accounts. Please try
            again.
          </Typography>
          <Typography variant="body-1" as="p" class="my-3" v-else>
            Your {{ applicationName }} and Slack accounts have been successfully linked.
          </Typography>
        </v-card-text>
        <v-card-actions>
          <!-- <v-spacer /> -->
          <Button
            color="brandPrimary"
            class="mx-auto mt-4"
            style="width: 170px"
            @click="(slackDialog = false), (slackError = false)"
          >
            Close
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- GDPR Dialog window -->
    <GdprRequestDialog v-model="gdprDialog" />
    <!-- SMS Troubleshooting Dialog window -->
    <v-dialog width="500" v-model="dialog.smsTroubleshoot">
      <v-card rounded="0" class="d-flex justify-center flex-column pa-6">
        <div class="d-flex justify-space-between align-center mx-6 mb-3">
          <v-icon color="error" x-large class="exit-warning-icon mr-2">mdi-alert</v-icon>
          <v-card-title class="word-break align-text-left exit-warning-text">
            <Typography variant="h-5" as="span">
              Having issues receiving {{ applicationName }} texts?
            </Typography>
          </v-card-title>
        </div>
        <Typography variant="body-1" as="span" class="word-break exit-warning-text">
          You might be unsubscribed from our number. <br />Please try texting "START" to +1 (205)
          635 - 7282.
        </Typography>
        <v-card-actions class="mt-4">
          <Button
            color="brandCyan"
            class="white--text mx-auto"
            style="width: 130px"
            @click="dialog.smsTroubleshoot = false"
          >
            Close
          </Button>
        </v-card-actions>
      </v-card></v-dialog
    >
    <!-- Image Cropping dialog -->
    <v-dialog persistent v-model="dialogImageCropper" width="600">
      <v-card rounded="0" class="px-12">
        <div class="d-flex align-center">
          <v-icon class="mr-2">mdi-camera</v-icon>
          <v-card-title class="word-break px-0 mx-0">
            <Typography variant="subtitle-1" as="span">
              Please crop the image below to a 1 : 1 aspect ratio.
            </Typography>
          </v-card-title>
        </div>
        <cropper
          ref="cropper"
          :src="imageUrlToCrop"
          :stencil-props="{
            aspectRatio: 1 / 1,
          }"
        />
        <v-card-actions class="pt-4">
          <v-spacer />
          <Button text :disabled="user.isSelectingProfilePic" @click="closeImageCropDialog(false)">
            Cancel
          </Button>
          <Button
            text
            color="brandPrimary"
            :loading="user.isSelectingProfilePic"
            @click="closeImageCropDialog(true)"
          >
            Save
          </Button>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <!-- Token for displaying token -->
    <v-dialog v-model="userTokenDialog" hide-overlay width="600">
      <v-card color="primary" dark>
        <v-card-text>
          <Typography variant="body-2" as="span" class="white--text">{{ token }}</Typography>
          <!-- <v-divider></v-divider>
            <v-icon @click="copyToken">mdi-eye-settings-outline</v-icon> -->
        </v-card-text>
      </v-card></v-dialog
    >
    <!-- Dialog used for displaying loading message. -->
    <LoadingDialog
      :showDialog="showLoadingDialog"
      :header="loadingHeaderMessage"
      :line1="loadingMessageLine1"
      :line2="loadingMessageLine2"
    ></LoadingDialog>
  </div>
</template>

<script>
import UserService from "../services/UserService";
import OnboardingService from "@/services/OnboardingService.js";
import BudgetService from "@/services/BudgetService.js";
import BadgeService from "../services/BadgeServiceV2";
import SlackService from "../services/SlackService";

import LoadingDialog from "../components/loadingDialog";
import SlackDownload from "@/components/SlackDownload";
import PhoneNumber from "@/components/PhoneNumber";
import Alert from "@/components/generic/Alert.vue";
import Button from "@/components/generic/Button.vue";
import whitelabelMixin from "@/mixins/whitelabelMixin";
import Typography from "@/components/generic/Typography.vue";
import GdprRequestDialog from "@/components/GdprRequestDialog.vue";
import TaxInfoSection from "@/components/profile/TaxInfoSection.vue";
import UserServiceV3 from "@/services/UserServiceV3";
import featureFlags from "@/mixins/featureFlags";
import isTaxInfoConfigured from "@/mixins/isTaxInfoConfigured";
import informOverrideMixin from "@/mixins/informOverride";

import { Cropper } from "vue-advanced-cropper";
import "vue-advanced-cropper/dist/style.css";

import moment from "moment";
import { languages, emailRegex, formRules, states, countries } from "@/shared_data/data.js";

import { mapState, mapGetters } from "vuex";

export default {
  name: "Profile",
  title: "Profile",
  components: {
    LoadingDialog,
    SlackDownload,
    Cropper,
    PhoneNumber,
    Alert,
    Button,
    Typography,
    GdprRequestDialog,
    TaxInfoSection,
  },
  mixins: [featureFlags, isTaxInfoConfigured, whitelabelMixin, informOverrideMixin],
  props: { initialPageIndexToLoad: Number },
  data() {
    return {
      badgeService: new BadgeService(),
      userProfile: null,
      userTokenDialog: false,
      showLoadingDialog: false,
      loadingHeaderMessage: null,
      loadingMessageLine1: null,
      loadingMessageLine2: null,
      submittingChanges: false,
      languages: languages,
      states,
      countries,
      gdprDialog: false,
      dialogImageCropper: false,
      image: null,
      imageUrlToCrop: null,
      imageUrlFileName: null,

      emailValidation: emailRegex,
      formRules,

      dialog: {
        smsTroubleshoot: false,
      },
      loading: {
        badges: false,
      },
      badges: [],
      awards: [],
      user: {
        firstName: null,
        lastName: null,
        displayName: null,
        businessEmail: null,
        businessPhone: null,
        personalEmail: null,
        personalPhone: null,
        formattedBusinessPhone: null,
        formattedPersonalPhone: null,
        birthday: null,
        birthdayMenu: false,
        homeAddress: null,
        homeAddress2: null,
        homeCity: null,
        homeState: null,
        homePostalCode: null,
        homeCountry: null,
        otherHomeCountry: null,
        profilePicUrl: null,
        isSelectingProfilePic: false,
        language: null,
      },
      communicationPreferences: {
        emailPreference: true,
        emailPreferenceId: null,
        originalEemailPreference: null,
        smsPreference: true,
        smsPreferenceId: null,
        originalSmsPreference: null,
        pushPreference: true,
        pushPreferenceId: null,
        originalPushPreference: null,
      },
      // businessCountryCode: { code: null, value: null },
      // personalCountryCode: { code: null, value: null },
      businessPhoneNumberValid: false,
      personalPhoneNumberValid: false,
      // Raw text currently sitting in the Additional phone number field. Distinct from
      // `user.personalPhone`, which is the value the profile was LOADED with and is the
      // `:value` prop the PhoneNumber child resets itself from - it must not track keystrokes.
      personalPhoneInput: null,
      submittingPasswordRequest: false,
      passwordResetDialog: false,
      emailErrorDialog: false,
      slackDialog: false,
      slackError: false,
      validForm1: false,
      assetCodeToOrder: null,
      /** From user-client when profile is Inform-managed (Basic Info fields read-only). */
      informManaged: false,
      hasTaxInfo: false,
      savedTaxTypeCode: null,
      taxInfoRequired: false,
      taxPanel: 0,
      taxData: {},
      taxFormValid: true,
      taxFormDirty: false,
      showNavigationDialog: false,
      pendingNavigation: null,
    };
  },
  beforeRouteLeave(to, from, next) {
    if (this.checkIfAnythingChanged || this.taxFormDirty) {
      this.pendingNavigation = next;
      this.showNavigationDialog = true;
    } else {
      next();
    }
  },
  beforeMount() {},
  created() {
    console.log("profile created");
    if (this.$auth) {
      if (this.$route.name == "profileslacksuccess") this.slackDialog = true;
      else if (this.$route.name == "slacksetupuser" && this.$route.query.correlationId) {
        console.log("Connecting platform user to slack user ", this.$route.query.correlationId);
        this.storeSlackAssociation();
      }
      this.getUserProfile();
      this.getBadgesByUser();
      this.getAwards();
      this.$store.dispatch("setBalances", this.$auth.user.sub);
      console.log(this.initialPageIndexToLoad);
      if (
        this.initialPageIndexToLoad !== undefined &&
        this.initialPageIndexToLoad !== null &&
        !isNaN(this.initialPageIndexToLoad)
      ) {
        this.tab = this.initialPageIndexToLoad;
      } else {
        this.tab = 0;
      }
    }
  },
  mounted() {},
  methods: {
    /**
     * Whether one input must be read-only, given the Engage property it writes.
     *
     * An Inform-managed record used to lock every field. It now locks only the ones the
     * client's feed actually owns — editing those here would be reverted by the next
     * sync pass, silently, which is the only thing worth preventing. A field Engage
     * owns, whether because nothing is mapped onto it or because it is in the tenant's
     * override list, stays editable.
     *
     * @param {string} engageProperty
     * @returns {boolean}
     */
    fieldReadonly(engageProperty) {
      if (!this.informManaged) return false;
      return this.isManagedFieldReadonly(engageProperty);
    },
    openMailto(href) {
      window.location.href = href;
    },
    async getUserProfile() {
      this.loadingHeaderMessage = "Loading profile";
      this.loadingMessageLine1 = "Please wait";
      this.loadingMessageLine2 = "This should just take a second!";
      // We only want to show this if we're not showing the Slack success
      if (!this.slackDialog) this.showLoadingDialog = true;
      try {
        // Sending version 2 means it excludes wallet tables
        const response = await UserService.getUserProfileV3(this.magicLinkToken, { version: 2 });

        console.log("response: ", response);
        console.log(response);
        // this.businessCountryCode.code = response.businessCountry;
        // this.personalCountryCode.code = response.homeCountry;

        // if (
        //   response.businessPhone !== null &&
        //   response.businessPhone.includes("+")
        // ) {
        //   var businessPhone = response.businessPhone.substring(
        //     response.businessPhone.indexOf("-") + 1
        //   );
        //   // this.user.formattedBusinessPhone =
        // } else {
        const businessPhone = response.businessPhone;
        // }
        // if (
        //   response.personalPhone !== null &&
        //   response.personalPhone.includes("+")
        // ) {
        //   var personalPhone = response.personalPhone.substring(
        //     response.personalPhone.indexOf("-") + 1
        //   );
        // } else {
        const personalPhone = response.personalPhone;
        // }

        // Fetch tax status before assigning userProfile so the TaxInfoSection prop watcher
        // (triggered by the userProfile reassignment below) sees up-to-date hasTaxInfo/savedTaxTypeCode
        // in the same tick, instead of racing the reassignment and briefly showing a blank SSN/EIN field.
        // Gates on the freshly-fetched `response.Client`, not `this.userProfile` (still null on first
        // mount) - `canCollectTaxInfo` reads the latter, so checking it here would silently skip this
        // call on initial load.
        if (this.isTaxInfoEnabled() && !!response?.Client?.taxInfoEnabled) {
          try {
            const taxStatus = await UserServiceV3.getTaxInfoStatus();
            this.hasTaxInfo = taxStatus?.result?.hasTaxInfo ?? false;
            this.savedTaxTypeCode = taxStatus?.result?.typeCode ?? null;
            this.taxInfoRequired = taxStatus?.result?.taxInfoRequired ?? false;
          } catch (e) {
            console.log("Error fetching tax info status:", e);
            // Fail open so a stale `true` from a prior fetch can't
            // keep blocking Save on a transient error.
            this.taxInfoRequired = false;
          }
        }

        this.userProfile = response;
        this.user.firstName = response.firstName;
        this.user.lastName = response.lastName;
        this.user.displayName = response.displayName;
        this.user.birthday = response.birthday;
        this.user.businessEmail = response.businessEmail;

        this.user.businessPhone = businessPhone;
        this.user.personalEmail = response.personalEmail;
        this.user.personalPhone = personalPhone;
        // Seed the field-content mirror so the "typed something, but it isn't a valid
        // number" guard in disableSaveButton behaves identically on a freshly loaded
        // profile (including one whose stored number no longer parses).
        this.personalPhoneInput = personalPhone;
        this.user.profilePicUrl = response.profilePicUrl;
        this.user.language = response.language;
        this.user.homeAddress = response.homeAddress;
        this.user.homeAddress2 = response.homeAddress2;
        this.user.homeCity = response.homeCity;
        this.user.homeState = response.homeState;
        this.user.homePostalCode = response.homePostalCode;
        this.hydrateHomeCountry(response.homeCountry);

        if (response.UserCommunicationPreferences) {
          const commPref = response.UserCommunicationPreferences;
          const sms = commPref.find((x) => x.deliveryMethod === "sms");
          if (sms) {
            this.communicationPreferences.smsPreference = sms.value;
            this.communicationPreferences.smsPreferenceId = sms.communicationPreferenceId;
            this.communicationPreferences.originalSmsPreference = sms.value;
          } else {
            this.communicationPreferences.smsPreference = false;
            this.communicationPreferences.smsPreferenceId = null;
            this.communicationPreferences.originalSmsPreference = null;
          }

          const email = commPref.find((x) => x.deliveryMethod === "email");
          if (email) {
            this.communicationPreferences.emailPreference = email.value;
            this.communicationPreferences.emailPreferenceId = email.communicationPreferenceId;
            this.communicationPreferences.originalEmailPreference = email.value;
          } else {
            this.communicationPreferences.emailPreference = true;
            this.communicationPreferences.emailPreferenceId = null;
            this.communicationPreferences.originalEmailPreference = null;
          }

          const push = commPref.find((x) => x.deliveryMethod === "push");
          if (push) {
            this.communicationPreferences.pushPreference = push.value;
            this.communicationPreferences.pushPreferenceId = push.communicationPreferenceId;
            this.communicationPreferences.originalPushPreference = push.value;
          } else {
            this.communicationPreferences.pushPreference = true;
            this.communicationPreferences.pushPreferenceId = null;
            this.communicationPreferences.originalPushPreference = null;
          }
        }

        this.informManaged = response.informManaged;
        // Only a managed record needs it, and it decides which fields stay editable.
        if (this.informManaged) {
          await this.loadEngageConfiguration(response.clientId ?? this.user?.clientId);
        }
      } catch (error) {
        console.log("Error! " + (error && error.response));
      } finally {
        this.showLoadingDialog = false;
      }
    },
    loadBadgePage(badgeId) {
      this.tab = 2;
      console.log(badgeId);
      var badgeIndex = this.badges.findIndex((x) => x.badgeId == badgeId);
      console.log(badgeIndex);
      if (badgeIndex !== -1) {
        this.badges[badgeIndex].showDescription = true;
      }
    },
    getAwards() {
      BudgetService.getAwards()
        .then((response) => {
          console.log("Awards: ", response);
          var awards = response.filter((x) => x.status != "Deleted");

          awards.forEach(function (award) {
            award.displayName = award.awardDisplayName ? award.awardDisplayName : award.awardName;
          });
          this.awards = awards;
          console.log(this.awards);

          this.loadingAwardTable = false;
        })
        .catch((error) => {
          console.log("Error!" + error);
        });
    },
    async copyToken() {
      await navigator.clipboard.writeText(this.token);
      // const el = document.createElement("textarea");
      // el.value = this.token;
      // document.body.appendChild(el);
      // console.log(el.value);
      // el.select();
      // document.execCommand("copy");
      // console.log("copioed");
      //document.body.removeChild(el);
    },
    logout() {
      this.$auth.logout({
        returnTo: window.location.origin,
      });
    },
    openWallet() {
      this.$store.dispatch("setWalletMenuScreen", "main");
      this.$store.dispatch("setDisplayWalletMenu", true);
    },
    downloadTerms() {
      OnboardingService.downloadLegalDoc("terms");
    },
    downloadPrivacy() {
      OnboardingService.downloadLegalDoc("privacy");
    },
    displayImageSelector() {
      this.$refs.uploader.click();
    },
    closeImageCropDialog(submitPressed) {
      //submitPressed is so we can use one function for both dialog buttons
      //If they clicked submit, submitPressed is true
      //If they clicked cancel, submitPressed is false
      var clientId = this.userProfile.clientId;
      if (submitPressed) {
        this.user.isSelectingProfilePic = true;
        const { canvas } = this.$refs.cropper.getResult();
        canvas.toBlob((blob) => {
          // Do something with blob: upload to a server, download and etc.
          var file = new File([blob], this.imageUrlFileName);
          console.log("Uploading new profile picture: ", file);
          UserService.uploadProfilePicture(this.userProfile.userId, file, clientId)
            .then((imageResponse) => {
              console.log("Image upload response: ", imageResponse);
              // We got an image url back so now we update the user object with this image
              this.user.profilePicUrl = imageResponse.filePath;
              this.$store.state.userProfile.profilePicUrl = imageResponse.filePath;
              this.user.isSelectingProfilePic = false;
              //Regardless, we close dialog
              this.dialogImageCropper = false;
              this.imageURLToCrop = null;
              this.imageURLFileName = null;
            })
            .catch((error) => {
              console.log("Error uploading image: ", error);
              //Regardless, we close dialog
              this.dialogImageCropper = false;
              this.imageURLToCrop = null;
              this.imageURLFileName = null;
              this.user.isSelectingProfilePic = false;
            });
        });
      } else {
        //Regardless, we close dialog
        this.dialogImageCropper = false;
        this.imageUrlToCrop = null;
        this.imageUrlFileName = null;
      }
    },
    imageFileChanged(e) {
      // this.user.isSelectingProfilePic = true;
      if (e.target.files.length > 0) {
        this.imageUrlToCrop = URL.createObjectURL(e.target.files[0]);
        this.imageUrlFileName = e.target.files[0].name;
        this.dialogImageCropper = true;
      }
    },
    dragAndDropImageFileChanged(e) {
      if (e.dataTransfer.files.length > 0) {
        // Load file into temp variable for cropping
        this.imageUrlToCrop = URL.createObjectURL(e.dataTransfer.files[0]);
        this.imageUrlFileName = e.dataTransfer.files[0].name;

        this.dialogImageCropper = true;
      }
    },
    deleteProfilePicture() {
      this.user.profilePicUrl = null;
      UserService.deleteProfilePicture(this.userProfile.userId, this.userProfile.clientId)
        .then((imageResponse) => {
          console.log(imageResponse);
          this.$store.state.userProfile.profilePicUrl = null;
        })
        .catch((error) => {
          this.showLoadingDialog = false;
          console.log("Error deleting image: ", error);
        });
    },
    preventNav(event) {
      if (this.checkIfAnythingChanged) {
        event.preventDefault();
        event.returnValue = "";
      }
    },
    businessPhoneValidation: function ({ number, isValid }) {
      // Do stuff with the arguments passed by the vue-tel-input component
      if (isValid) {
        this.user.formattedBusinessPhone = number; //rfc3966.replace("tel:", "");
        this.businessPhoneNumberValid = true;
      } else {
        this.businessPhoneNumberValid = false;
      }
    },
    updateBusinessPhone: function () {
      if (!this.user.businessPhone || !this.businessPhoneNumberValid) {
        this.user.formattedBusinessPhone = null;
        this.businessPhoneNumberValid = false;
      }
    },
    personalPhoneValidation: function (phoneObject) {
      // `validate` fires when validity flips, and when the field resets from a newly
      // loaded value. That reset emits no `input`, so this is the path that first stages
      // a stored valid number on load.
      this.stagePersonalPhone(phoneObject);
    },
    updatePersonalPhone: function (num, validation, rawInput) {
      // PhoneNumber emits `input` on every keystroke carrying the freshly parsed number,
      // but only emits `validate` when the validity flag CHANGES. Replacing one valid
      // number with a different valid number in a single input event - pasting over a
      // selection, or retyping a single selected character - never flips that flag, so
      // `validate` never fires. Taking the number only from `validate` therefore left
      // `formattedPersonalPhone` holding the previously validated number, which is both
      // what `checkIfAnythingChanged` compares and what `saveProfile` sends. Read the
      // parsed object off the `input` event, as UserCreatorWidgetV2.updateBusinessPhone does.
      this.personalPhoneInput = typeof rawInput === "string" ? rawInput : "";
      this.stagePersonalPhone(validation && typeof validation === "object" ? validation : num);
    },
    // The only writer of the staged Additional phone number and its validity flag, so the
    // `input` and `validate` paths cannot drift apart. saveProfile sends this number;
    // anything that isn't a valid parse stages null.
    stagePersonalPhone: function (parsed) {
      const isValid = !!(parsed && parsed.isValid);
      this.personalPhoneNumberValid = isValid;
      this.user.formattedPersonalPhone = isValid ? parsed.number : null;
    },
    resetPassword() {
      this.submittingPasswordRequest = true;
      UserService.resetPassword(this.user.businessEmail).then((response) => {
        console.log(response);
        this.submittingPasswordRequest = false;
        this.passwordResetDialog = true;
      });
    },
    onTaxUpdate({ data, valid, dirty }) {
      this.taxData = data;
      this.taxFormValid = valid;
      this.taxFormDirty = dirty;
    },
    async saveProfile() {
      this.loadingHeaderMessage = "Updating profile";
      this.loadingMessageLine1 = "Please wait";
      this.loadingMessageLine2 = "This should just take a second!";
      this.showLoadingDialog = true;

      this.resetOnTaxpayerChange();

      var body = {
        personalPhone: this.user.formattedPersonalPhone,
        firstName: this.user.firstName,
        lastName: this.user.lastName,
        displayName: this.user.displayName,
        birthday: this.user.birthday,
        personalEmail:
          this.user.personalEmail == null ? null : this.user.personalEmail.toLowerCase(),
        language: this.user.language,
        homeAddress: this.user.homeAddress,
        homeAddress2: this.user.homeAddress2,
        homeCity: this.user.homeCity,
        homeState: this.user.homeState,
        homePostalCode: this.user.homePostalCode,
        homeCountry: this.resolvedHomeCountry,
        ...this.taxData,
      };
      console.log(body);

      try {
        // If the user has tax info and the tax type code has changed, we need to clear the tax info
        if (
          this.canCollectTaxInfo &&
          this.hasTaxInfo &&
          this.savedTaxTypeCode !== this.taxData.typeCode &&
          this.savedTaxTypeCode
        ) {
          // If the user has tax info and the tax type code has changed, we need to clear the tax info
          try {
            await UserServiceV3.clearTaxInfo({
              reason:
                "Tax type code changed from " +
                this.savedTaxTypeCode +
                " to " +
                this.taxData.typeCode,
            });
          } catch (err) {
            console.error(
              "Failed to clear tax info:",
              err?.response?.data?.message || err?.message
            );
            this.$store.dispatch("showError", {
              title: "Tax Info not saved",
              message: "Failed to switch your tax information type. Please try again.",
            });
            return false;
          }
        }

        const user = await UserService.updateUser(
          this.userProfile.userId,
          body,
          this.magicLinkToken
        );

        console.log("Updated user:", user);

        this.$store.dispatch("setUser", this.userProfile.userId);

        // Save tax id if a fresh one was entered (new user, or SSN/EIN type switch)
        if (this.taxData.taxId) {
          try {
            await UserServiceV3.saveTaxInfo({
              typeCode: this.taxData.typeCode,
              taxNumber: this.taxData.taxId,
            });
            this.savedTaxTypeCode = this.taxData.typeCode;
            this.hasTaxInfo = true;
            this.taxFormDirty = false;
          } catch (err) {
            console.error("Failed to save tax info:", err?.response?.data?.message || err?.message);
            this.$store.dispatch("showError", {
              title: "Tax Info Not Saved",
              message: "Failed to save tax information. Please try again.",
            });
            return false;
          }
        }
        await this.getUserProfile();
        return true;
      } catch (error) {
        console.error("Failed to update profile:", error);
        this.$store.dispatch("showError", {
          title: "Profile Update Failed",
          message: "Failed to update profile. Please try again.",
        });
        return false;
      } finally {
        this.showLoadingDialog = false;
      }
    },

    resetOnTaxpayerChange() {
      const SOLE_PROPRIETOR_SINGLE_MEMBER_LLC = "SOLE_PROPRIETOR_SINGLE_MEMBER_LLC";
      if (!this.canCollectTaxInfo) return;

      // Always strip fields hidden by the currently selected taxpayer type/classification,
      // so values entered during an abandoned edit (then switched away from) aren't persisted.
      if (this.taxData.taxpayerType === "Business") {
        if (this.taxData.taxClassification !== SOLE_PROPRIETOR_SINGLE_MEMBER_LLC) {
          this.taxData.fullLegalName = null;
        } else {
          this.taxData.taxTradeName = null;
        }
      } else {
        this.taxData.taxClassification = null;
        this.taxData.taxBusinessName = null;
        this.taxData.taxTradeName = null;
      }
    },

    // Stay on the profile page; dismiss the dialog without discarding pending navigation.
    handleNavigationCancel() {
      this.showNavigationDialog = false;
      this.pendingNavigation = null;
    },
    // Abandon unsaved changes and continue to wherever the user was navigating.
    handleNavigationDiscard() {
      this.showNavigationDialog = false;
      if (this.pendingNavigation) {
        this.pendingNavigation();
        this.pendingNavigation = null;
      }
    },
    saveCommunicationPreferences() {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Saving Changes";
      this.loadingMessageLine1 = "Your new preferences should be reflected immediately.";
      this.loadingMessageLine2 = null;
      var preferences = [
        {
          userId: this.userProfile.userId,
          communicationType: null,
          deliveryMethod: "sms",
          value: this.communicationPreferences.smsPreference,
          updatedBy: this.userProfile.userId,
        },
        {
          userId: this.userProfile.userId,
          communicationType: null,
          deliveryMethod: "email",
          value: this.communicationPreferences.emailPreference,
          updatedBy: this.userProfile.userId,
        },
        {
          userId: this.userProfile.userId,
          communicationType: null,
          deliveryMethod: "push",
          value: this.communicationPreferences.pushPreference,
          updatedBy: this.userProfile.userId,
        },
      ];

      //Then we have to check if we originally stored ID's for user supplemental records in order to update them
      if (this.communicationPreferences.smsPreferenceId) {
        preferences[0].communicationPreferenceId = this.communicationPreferences.smsPreferenceId;
      } else {
        preferences[0].createdBy = this.userProfile.userId;
      }
      if (this.communicationPreferences.emailPreferenceId) {
        preferences[1].communicationPreferenceId = this.communicationPreferences.emailPreferenceId;
      } else {
        preferences[1].createdBy = this.userProfile.userId;
      }
      if (this.communicationPreferences.pushPreferenceId) {
        preferences[2].communicationPreferenceId = this.communicationPreferences.pushPreferenceId;
      } else {
        preferences[2].createdBy = this.userProfile.userId;
      }
      UserService.bulkUpdateUserCommunicationPreferences(preferences, this.magicLinkToken).then(
        (pref) => {
          this.showLoadingDialog = false;
          console.log("Comm Pref response: ", pref);
          // Now we just update the "original" variables that we use to detect a change
          // We also update the preference ids if they didn't exist before
          this.communicationPreferences.originalPushPreference =
            this.communicationPreferences.pushPreference;
          this.communicationPreferences.originalEmailPreference =
            this.communicationPreferences.emailPreference;
          this.communicationPreferences.originalSmsPreference =
            this.communicationPreferences.smsPreference;

          if (!this.communicationPreferences.smsPreferenceId) {
            let smsRes = pref.find((x) => x.deliveryMethod == "sms");
            if (smsRes)
              this.communicationPreferences.smsPreferenceId = smsRes.communicationPreferenceId;
          }
          if (!this.communicationPreferences.emailPreferenceId) {
            let emailRes = pref.find((x) => x.deliveryMethod == "email");
            if (emailRes)
              this.communicationPreferences.emailPreferenceId = emailRes.communicationPreferenceId;
          }
          if (!this.communicationPreferences.pushPreferenceId) {
            let pushRes = pref.find((x) => x.deliveryMethod == "push");
            if (pushRes)
              this.communicationPreferences.pushPreferenceId = pushRes.communicationPreferenceId;
          }
        }
      );
    },
    resetOnboardingFlow() {
      this.showLoadingDialog = true;
      this.loadingHeaderMessage = "Resetting Onboarding";
      this.loadingMessageLine1 = "After this is done,";
      this.loadingMessageLine2 = "you will be taken back to onboarding.";
      var body = {
        firstLogin: true,
      };
      UserService.updateUser(this.userProfile.userId, body, this.magicLinkToken)
        .then((user) => {
          console.log("Successfully updated user ", user);
          this.$store.state.userProfile.firstLogin = true;
          this.showLoadingDialog = false;
        })
        .catch((error) => {
          console.error("Error resetting onboarding: ", error);
          this.showLoadingDialog = false;
        });
    },
    async getBadgesByUser() {
      console.log("Fetching badges by user");
      this.loading.badges = true;
      try {
        const badgeResult = await this.badgeService.getUserBadgesForUser(this.$auth.user.sub);
        console.log("Badges returned: ", badgeResult);
        badgeResult.result.rows.forEach((badge) => {
          badge.formattedDate = moment(badge.createDate).local().format("MMMM D, YYYY");
          badge.showDescription = false;
        });
        this.badges = badgeResult.result.rows;
      } catch (e) {
        console.log("Error fetching badges earned: ", e);
      } finally {
        this.loading.badges = false;
      }
    },
    saveBirthday(date) {
      if (this.informManaged) return;
      this.$refs.birthdayMenu.save(date);
    },
    hydrateHomeCountry(homeCountry) {
      if (homeCountry == null) {
        this.user.homeCountry = null;
        this.user.otherHomeCountry = null;
        return;
      }
      if (this.countries.some((c) => c.code === homeCountry && c.code !== "00")) {
        this.user.homeCountry = homeCountry;
        this.user.otherHomeCountry = null;
        return;
      }
      this.user.homeCountry = "00";
      this.user.otherHomeCountry = homeCountry;
    },
    homeAddressFieldLabel(label) {
      return this.addressUnitStarted ? `${label}*` : label;
    },
    homeAddressRules(label) {
      const rules = [
        (v) => !v || v.length < 255 || "This should be shorter",
        this.formRules.noHtml(label),
      ];
      if (this.addressUnitStarted) {
        rules.unshift((v) => !!(v && String(v).trim()) || `${label} is required`);
      }
      return rules;
    },
    hasAddressValue(value) {
      return !!(value && String(value).trim());
    },
    formatDate(date) {
      if (date) {
        return moment(date).local().format("MMM DD, YYYY h:mm a");
      } else {
        return null;
      }
    },
    async storeSlackAssociation() {
      if (process.env.VUE_APP_SLACK_URL) {
        console.log("Storing slack assocation with User (passing query param)");
        try {
          await SlackService.storeSlackUserAssociation(this.$route.query.correlationId);
          this.slackDialog = true;
          this.clearSlackUrl();
        } catch (err) {
          this.slackDialog = true;
          this.slackError = true;
        }
      }
      // else if (
      //   this.slackLanguage &&
      //   process.env.VUE_APP_SLACK_URL &&
      //   passQueryParam &&
      //   this.$route.query.correlationId
      // ) {
      //   console.log(
      //     "Storing slack assocation with Client (passing query param)"
      //   );
      //   SlackService.storeSlackWorkspaceAssociation(
      //     this.$route.query.correlationId
      //   );
      // }
    },
    clearSlackUrl() {
      let query = Object.assign({}, this.$route.query);
      if (query.correlationId) {
        delete query.correlationId;
        this.$router.replace({ query });
      }
    },
  },
  computed: {
    ...mapState(["token", "balances", "profilePageTab", "magicLinkToken", "permissions"]),
    ...mapGetters(["hasPermissionToManageClients"]),
    languageOptions() {
      const code = this.user.language;
      if (!code || this.languages.some((language) => language.code === code)) return this.languages;
      return [...this.languages, { name: code, code }];
    },
    informAlertMessage() {
      // ADO 186742. Birthday belongs in this list like any other field. It used to be
      // excluded because it was locked categorically, so it could never be the reason
      // anything was still editable - which stopped being true when ownership moved to
      // the tenant's own configuration.
      const editableFields = [
        "firstName",
        "lastName",
        "displayName",
        "birthday",
        "personalEmail",
        "personalPhone",
        "homeAddress",
        "homeAddress2",
        "homeCity",
        "homeState",
        "homePostalCode",
        "homeCountry",
      ];
      if (editableFields.some((field) => !this.fieldReadonly(field))) {
        return "Some of your profile details are managed by your organization.";
      }
      return "Your profile details are managed by your organization. You can still update your profile photo & preferred language.";
    },
    addressUnitStarted() {
      return (
        this.hasAddressValue(this.user.homeAddress) ||
        this.hasAddressValue(this.user.homeCity) ||
        this.hasAddressValue(this.user.homeState) ||
        this.hasAddressValue(this.user.homePostalCode) ||
        this.hasAddressValue(this.user.homeCountry) ||
        this.hasAddressValue(this.user.otherHomeCountry)
      );
    },
    isUSHomeAddress() {
      return !this.user.homeCountry || this.user.homeCountry === "US";
    },
    isOtherHomeCountry() {
      return this.user.homeCountry === "00";
    },
    resolvedHomeCountry() {
      if (this.user.homeCountry === "00") {
        return this.user.otherHomeCountry ? this.user.otherHomeCountry.toUpperCase() : null;
      }
      return this.user.homeCountry;
    },
    otherHomeCountryRules() {
      const rules = [(v) => !v || v.length === 2 || "Country code should be 2 letters"];
      if (this.addressUnitStarted) {
        rules.unshift((v) => !!(v && String(v).trim()) || "Country (2 digit code) is required");
      }
      return rules;
    },
    disableSaveButton() {
      if (
        !this.validForm1 ||
        (!this.businessPhoneNumberValid && this.user.businessPhone) ||
        // What's in the box now - not what the profile loaded with. Using
        // user.personalPhone here made an emptied field look like it still held an
        // unparseable number, so a saved number could never be removed.
        (!this.personalPhoneNumberValid && this.personalPhoneInput) ||
        (this.canCollectTaxInfo &&
          (this.taxInfoRequired || this.taxFormDirty) &&
          !this.taxFormValid)
      )
        return true;

      // Nothing to save if neither the basic info nor the tax section changed
      if (!this.checkIfAnythingChanged && !this.taxFormDirty) return true;

      return false;
    },
    displaySlackButton() {
      if (this.userProfile && this.$route.query.slack) return true;
      return false;
    },
    checkIfAnythingChanged() {
      if (
        this.userProfile &&
        (this.userProfile.firstName !== this.user.firstName ||
          this.userProfile.lastName !== this.user.lastName ||
          this.userProfile.businessEmail !== this.user.businessEmail ||
          this.userProfile.businessPhone !== this.user.formattedBusinessPhone ||
          this.userProfile.personalEmail != this.user.personalEmail ||
          this.userProfile.personalPhone != this.user.formattedPersonalPhone ||
          this.userProfile.displayName !== this.user.displayName ||
          this.userProfile.birthday !== this.user.birthday ||
          this.userProfile.language !== this.user.language ||
          this.userProfile.homeAddress !== this.user.homeAddress ||
          this.userProfile.homeAddress2 !== this.user.homeAddress2 ||
          this.userProfile.homeCity !== this.user.homeCity ||
          this.userProfile.homeState !== this.user.homeState ||
          this.userProfile.homePostalCode !== this.user.homePostalCode ||
          this.userProfile.homeCountry !== this.resolvedHomeCountry)
      ) {
        return true;
      }
      return false;
    },
    checkIfCommPreferencesChanged() {
      console.log(this.communicationPreferences);
      if (
        this.communicationPreferences.smsPreference !=
          this.communicationPreferences.originalSmsPreference ||
        this.communicationPreferences.emailPreference !=
          this.communicationPreferences.originalEmailPreference ||
        this.communicationPreferences.pushPreference !=
          this.communicationPreferences.originalPushPreference
      ) {
        return true;
      }
      return false;
    },
    tab: {
      get: function () {
        return this.profilePageTab;
      },
      set: function (newValue) {
        this.$store.dispatch("setProfilePageTab", newValue);
      },
    },
    showDevButtons() {
      if (
        process.env.VUE_APP_ENVIRONMENT != "prod" ||
        (this.userProfile && this.userProfile.clientId == 1)
      ) {
        return true;
      }
      return false;
    },
  },
  watch: {
    "user.birthdayMenu"(val) {
      val &&
        setTimeout(() => {
          this.$refs.birthdayPicker.activePicker = "YEAR";
        });
    },
    "user.homeCountry"() {
      if (
        this.isUSHomeAddress &&
        this.user.homeState &&
        !this.states.some((s) => s.abbreviation === this.user.homeState)
      ) {
        this.user.homeState = null;
      }
    },
  },
};
</script>

<style scoped>
.border-radius {
  border-radius: 5px;
}

.brand-background {
  background-color: var(--v-brand-base);
}

.image-container {
  position: relative;
}

.delete-image-button {
  position: absolute;
  z-index: 1;
  right: 15px;
  top: -10px;
  padding: 0px;
  align-items: center;
  justify-content: center;
  padding: 3px;
  transition: 0.2s opacity;
  background-color: black;
}

.image-button {
  width: 150px;
  height: 150px;
}

.image-border {
  border: 2px solid;
  border-color: var(--v-grey-base);
}

.slider {
  width: 90%;
}

.preference-box {
  width: 100%;
  border: 1px solid;
  border-color: var(--v-grey-base);
}

.text-field {
  width: 50%;
  max-width: 50%;
}

/* Used for the check icon shown on the slack success popup */
.check-icon {
  min-width: 35px;
  width: 35px;
  min-height: 35px;
  height: 35px;
}

/* Remove padding from tax info expansion panel content */
.tax-info-content >>> .v-expansion-panel-content__wrap {
  padding: 0;
}

.profile-page {
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 24px;
  gap: 24px;
}

.profile-page__header {
  display: flex;
  align-items: center;
  gap: 16px;
  width: 100%;
}

.profile-page__body {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  gap: 24px;
  width: 100%;
}

.profile-page__sidebar {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  flex: 1 1 100%;
  gap: 16px;
}

.profile-page__content {
  flex: 1 1 0;
  min-width: 0;
}

.popular-links {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 16px;
}

.profile-tab {
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  padding-top: 24px;
  gap: 24px;
}

.profile-tab__form {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 0;
  gap: 24px;
}

.profile-tab__photo {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 0 0 208px;
}

.profile-form__heading {
  text-align: left;
  width: 100%;
}

.profile-form {
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 24px;
}

.profile-form__row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 24px;
}

.profile-form__row > * {
  flex: 1 1 100%;
  min-width: 0;
}

.profile-form__half {
  width: 100%;
}

.profile-form__save {
  display: flex;
  justify-content: center;
  width: 100%;
}

.home-address {
  display: flex;
  flex-direction: column;
  flex: 0 0 100%;
  width: 100%;
  gap: 24px;
}

.home-address__half {
  width: 100%;
  max-width: 100%;
}

.home-address__row {
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  gap: 24px;
}

.home-address__row > * {
  flex: 1 1 100%;
  min-width: 0;
}

@media (min-width: 600px) {
  .profile-form__row > * {
    flex: 1 1 0;
  }

  .profile-form__half,
  .home-address__half {
    width: calc((100% - 24px) / 2);
    max-width: calc((100% - 24px) / 2);
  }

  .home-address__row > * {
    flex: 1 1 0;
  }
}

@media (min-width: 960px) {
  .profile-page__sidebar {
    flex: 0 0 294px;
    max-width: 294px;
  }
}
</style>
